import {
  BoxProps,
  Center,
  chakra,
  ComponentWithAs,
  forwardRef,
  HTMLChakraProps,
  ThemingProps,
  useColorModeValue,
  useStyleConfig,
} from "@chakra-ui/react";
import {
  RemixiconComponentType,
  RiAlertLine,
  RiApps2Line,
  RiArchive2Line,
  RiArrowDownSLine,
  RiArrowUpSLine,
  RiBracketsLine,
  RiChat1Line,
  RiCheckboxCircleFill,
  RiCheckLine,
  RiCircleLine,
  RiCloseLine,
  RiComputerLine,
  RiDatabase2Line,
  RiDoorClosedLine,
  RiDoorOpenLine,
  RiEmotionSadLine,
  RiErrorWarningLine,
  RiFileCopyLine,
  RiGitClosePullRequestLine,
  RiGitMergeLine,
  RiGitPullRequestLine,
  RiGroup2Line,
  RiGroupLine,
  RiHotelLine,
  RiImportLine,
  RiKey2Line,
  RiLineChartLine,
  RiLoopLeftLine,
  RiMoonLine,
  RiNotification3Line,
  RiPieChartLine,
  RiPlug2Line,
  RiQuestionLine,
  RiRobot2Line,
  RiSearchLine,
  RiSettings4Line,
  RiSlackLine,
  RiSunLine,
  RiTestTubeLine,
  RiTriangleFill,
  RiUserLine,
} from "@remixicon/react";
import { FunctionComponent, SVGProps } from "react";

import BuFeatureAutoCleanup from "@/common/assets/auto-cleanup-line-icon.svg?react";
import BuAutoFeedbackSurveys from "@/common/assets/auto-feedback-surveys-icon.svg?react";
import BuFeatureCleanup from "@/common/assets/cleanup-broom-line-icon.svg?react";
import BuCompanyAttribute from "@/common/assets/company-attribute-icon.svg?react";
import BuCompanyList from "@/common/assets/company-list.svg?react";
import BuDoubleArrowSelect from "@/common/assets/double-arrow-select-icon.svg?react";
import BuEnvironmentDot from "@/common/assets/environment-dot-circle-fill.svg?react";
import BuEnvironments from "@/common/assets/environments-dots-circles-line.svg?react";
import BuFeatureMetric from "@/common/assets/feature-metric.svg?react";
import BuFeatureView from "@/common/assets/feature-views-icon.svg?react";
import BuFlag from "@/common/assets/flag.svg?react";
import GithubLogo from "@/common/assets/github-logo.svg?react";
import BuIdTag from "@/common/assets/id-tag.svg?react";
import BuLinearLogo from "@/common/assets/linear-logo.svg?react";
import BuTrackingEvent from "@/common/assets/pointer-press.svg?react";
import BuRemoteConfig from "@/common/assets/remote-config-icon.svg?react";
import BuVersionRollback from "@/common/assets/rollback-icon.svg?react";
import BuRollingTimeWindow from "@/common/assets/rolling-time-window-icon.svg?react";
import BuSatisfaction1Dark from "@/common/assets/satisfaction-1-icon-dark.svg?react";
import BuSatisfaction1 from "@/common/assets/satisfaction-1-icon.svg?react";
import BuSatisfaction2Dark from "@/common/assets/satisfaction-2-icon-dark.svg?react";
import BuSatisfaction2 from "@/common/assets/satisfaction-2-icon.svg?react";
import BuSatisfaction3Dark from "@/common/assets/satisfaction-3-icon-dark.svg?react";
import BuSatisfaction3 from "@/common/assets/satisfaction-3-icon.svg?react";
import BuSatisfaction4Dark from "@/common/assets/satisfaction-4-icon-dark.svg?react";
import BuSatisfaction4 from "@/common/assets/satisfaction-4-icon.svg?react";
import BuSatisfaction5Dark from "@/common/assets/satisfaction-5-icon-dark.svg?react";
import BuSatisfaction5 from "@/common/assets/satisfaction-5-icon.svg?react";
import BuSTARSFunnel from "@/common/assets/stars-icon.svg?react";
import StarsStateChurned from "@/common/assets/stars-state-churned.svg?react";
import StarsStateNever from "@/common/assets/stars-state-never.svg?react";
import StarsStateNone from "@/common/assets/stars-state-none.svg?react";
import StarsStateRetained from "@/common/assets/stars-state-retained.svg?react";
import StarsStateTried from "@/common/assets/stars-state-tried.svg?react";
import BuUnknownCompanyAvatar from "@/common/assets/unknown-company-avatar.svg?react";
import BuUnknownUserAvatar from "@/common/assets/unknown-user-avatar.svg?react";
import BuUserAttributeIcon from "@/common/assets/user-attribute-icon.svg?react";
import BuUserList from "@/common/assets/user-list.svg?react";
import { useColorContrast } from "@/common/hooks/useColorContrast";
import { CustomIconMap } from "@/common/utils/customIcons";
import { BuStagesLine } from "@/stage/components/StagesIcon";
import { IconSizes, IconSizesType } from "@/theme/Icon.theme";
import {
  EmojiFlatMap,
  EmojiKey,
  EmojiValueKey,
  isEmoji,
} from "@reflag/shared/emojis";
import { CustomIconKey } from "@reflag/shared/icons";

export const useDefaultIconColor = () => {
  return useColorModeValue("gray.300", "gray.600");
};

export function useResolveIconColor(iconColor?: string | null) {
  const defaultColor = useDefaultIconColor();
  const resolvedColor = iconColor ?? defaultColor;
  const adjustedColor = useColorContrast(resolvedColor);
  return {
    color: adjustedColor,
    isAdjusted: !!iconColor && adjustedColor !== iconColor,
  };
}

export type IconValueKey = CustomIconKey | EmojiValueKey;

export interface IconProps
  extends HTMLChakraProps<"div">, ThemingProps<"Icon"> {
  size?: IconSizesType;
}

export type IconType = ComponentWithAs<"div", IconPropsWithSize>;

type IconPropsWithSize = IconProps & { size?: IconSizesType };

const IconComponent = (
  svg: FunctionComponent<SVGProps<SVGSVGElement>> | RemixiconComponentType,
): IconType => {
  const comp = forwardRef<IconPropsWithSize, "div">((props, ref) => {
    const { size, ...rest } = props;
    const styles = useStyleConfig("Icon", { size });
    const Component = chakra(svg, {
      baseStyle: styles,
    });
    return <Component ref={ref} {...rest} />;
  });
  return comp;
};

export function CustomIconEmojiDisplay({
  iconKey,
  color: propColor,
  size = "md",
  ...rest
}: Omit<BoxProps, "children" | "color"> & {
  iconKey?: IconValueKey | null;
  color?: string | null;
  size?: IconSizesType;
}) {
  const { color } = useResolveIconColor(propColor);

  if (isEmoji(iconKey)) {
    const emoji = EmojiFlatMap[iconKey.slice(1, -1) as EmojiKey];
    const width = IconSizes[size].width;
    return (
      <Center boxSize={width} fontSize={width} {...rest}>
        {emoji}
      </Center>
    );
  }

  const Icon = IconComponent(
    CustomIconMap[iconKey ?? "Default"] ?? CustomIconMap.Default,
  );
  return <Icon color={color} size={size} {...rest} />;
}

// naming convention:
// - what it's used for and not what it looks like (if possible)
// - prefix component name with Ico (helps with intellisense)

// Common UI Icons
export const IcoClose = IconComponent(RiCloseLine);

// Arrows
export const IcoDoubleArrowSelect = IconComponent(BuDoubleArrowSelect);
export const IcoChevronDown = IconComponent(RiArrowDownSLine);
export const IcoChevronUp = IconComponent(RiArrowUpSLine);
export const IcoExpandArrow = IconComponent(RiTriangleFill);

// Organization settings
export const IcoOrganization = IconComponent(RiHotelLine);
export const IcoTeam = IconComponent(RiGroup2Line);
export const IcoUser = IconComponent(RiUserLine);
export const IcoIntegrations = IconComponent(RiPlug2Line);
export const IcoBilling = IconComponent(RiLineChartLine);
export const IcoExperiments = IconComponent(RiTestTubeLine);

// App settings
export const IcoApp = IconComponent(RiApps2Line);
export const IcoFeatureView = IconComponent(BuFeatureView);
export const IcoSegment = IconComponent(RiPieChartLine);
export const IcoEnvironments = IconComponent(BuEnvironments);
export const IcoImport = IconComponent(RiImportLine);
export const IcoStages = IconComponent(BuStagesLine);

// Environment settings
export const IcoAutoFeedbackSurveys = IconComponent(BuAutoFeedbackSurveys);
export const IcoExport = IconComponent(RiDatabase2Line);
export const IcoSlackMonotone = IconComponent(RiSlackLine);

// Feature
export const IcoFeature = IconComponent(BuFlag);
export const IcoFeatureAutoCleanup = IconComponent(BuFeatureAutoCleanup);
export const IcoFeatureCleanup = IconComponent(BuFeatureCleanup);
export const IcoRemoteConfig = IconComponent(BuRemoteConfig);
export const IcoVersionRollback = IconComponent(BuVersionRollback);
export const IcoArchive = IconComponent(RiArchive2Line);
export const IcoFeedback = IconComponent(RiChat1Line);

// STARS state
export const IcoStarsStateNone = IconComponent(StarsStateNone);
export const IcoStarsStateNever = IconComponent(StarsStateNever);
export const IcoStarsStateTried = IconComponent(StarsStateTried);
export const IcoStarsStateChurned = IconComponent(StarsStateChurned);
export const IcoStarsStateRetained = IconComponent(StarsStateRetained);

// Filters and data types
export const IcoCompanyAttribute = IconComponent(BuCompanyAttribute);
export const IcoUserAttribute = IconComponent(BuUserAttributeIcon);
export const IcoOtherContext = IconComponent(RiBracketsLine);
export const IcoSTARSFunnel = IconComponent(BuSTARSFunnel);
export const IcoCompanyList = IconComponent(BuCompanyList);
export const IcoUserList = IconComponent(BuUserList);
export const IcoFeatureMetric = IconComponent(BuFeatureMetric);
export const IcoIdTag = IconComponent(BuIdTag);
export const IcoKey = IconComponent(RiKey2Line);
export const IcoAccessExposure = IconComponent(RiDoorOpenLine);
export const IcoAccessCheck = IconComponent(RiDoorClosedLine);

// Satisfaction
export const IcoSatisfaction1 = IconComponent(BuSatisfaction1);
export const IcoSatisfaction1Dark = IconComponent(BuSatisfaction1Dark);
export const IcoSatisfaction2 = IconComponent(BuSatisfaction2);
export const IcoSatisfaction2Dark = IconComponent(BuSatisfaction2Dark);
export const IcoSatisfaction3 = IconComponent(BuSatisfaction3);
export const IcoSatisfaction3Dark = IconComponent(BuSatisfaction3Dark);
export const IcoSatisfaction4 = IconComponent(BuSatisfaction4);
export const IcoSatisfaction4Dark = IconComponent(BuSatisfaction4Dark);
export const IcoSatisfaction5 = IconComponent(BuSatisfaction5);
export const IcoSatisfaction5Dark = IconComponent(BuSatisfaction5Dark);

// Git
export const IcoGitOpenPR = IconComponent(RiGitPullRequestLine);
export const IcoGitMergedPR = IconComponent(RiGitMergeLine);
export const IcoGitClosedPR = IconComponent(RiGitClosePullRequestLine);

// Misc
export const IcoRollingTimeWindow = IconComponent(BuRollingTimeWindow);
export const IcoEnvironmentDot = IconComponent(BuEnvironmentDot);
export const IcoTrackingEvent = IconComponent(BuTrackingEvent);
export const IcoNotification = IconComponent(RiNotification3Line);
export const IcoUnknown = IconComponent(RiQuestionLine);
export const IcoUnknownCompanyAvatar = IconComponent(BuUnknownCompanyAvatar);
export const IcoUnknownUserAvatar = IconComponent(BuUnknownUserAvatar);
export const IcoServiceAccountAvatar = IconComponent(RiRobot2Line);
export const IcoGithub = IconComponent(GithubLogo);
export const IcoLinear = IconComponent(BuLinearLogo);
export const IcoCheck = IconComponent(RiCheckLine);
export const IcoCheckCircle = IconComponent(RiCheckboxCircleFill);
export const IcoCircle = IconComponent(RiCircleLine);
export const IcoSettings = IconComponent(RiSettings4Line);
export const IcoError = IconComponent(RiEmotionSadLine);
export const IcoWarning = IconComponent(RiAlertLine);
export const IcoAlert = IconComponent(RiErrorWarningLine);
export const IcoUsers = IconComponent(RiGroupLine);
export const IcoSearch = IconComponent(RiSearchLine);
export const IcoCopyToClipboard = IconComponent(RiFileCopyLine);
export const IcoRotate = IconComponent(RiLoopLeftLine);

// Color mode
export const IcoColorModeDark = IconComponent(RiMoonLine);
export const IcoColorModeLight = IconComponent(RiSunLine);
export const IcoColorModeSystem = IconComponent(RiComputerLine);
